import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { MediaUpload } from "../components/MediaUpload";
import { CATEGORIES } from "../components/CategoryFilter";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";

export default function NewProductPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    price: "",
    moq: "",
    stock: "",
    sale_type: "wholesale",
    seller_name: "",
    email: "",
    phone: "",
    media: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/products", form);
      toast.success(t("product_posted"));
      navigate(`/p/${res.data.id}`);
    } catch {
      toast.error(t("error_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    form.title.trim() &&
    form.description.trim() &&
    form.category &&
    form.price.trim() &&
    form.seller_name.trim() &&
    !submitting;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-16">
      <button
        data-testid="back-to-products"
        onClick={() => navigate("/products")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("tab_products")}
      </button>

      <div className="mb-10">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
          {t("nav_post_product")}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          {t("form_new_product")}
        </h1>
        <p className="mt-4 text-base text-muted-foreground max-w-xl leading-relaxed">
          {t("form_new_product_desc")}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-8 rounded-md border border-border bg-white p-6 md:p-10">
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
            {t("title_label")}
          </Label>
          <Input
            data-testid="prod-title-input"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder={t("product_title_placeholder")}
            className="h-11 rounded-md"
            required
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
            {t("description_label")}
          </Label>
          <Textarea
            data-testid="prod-description-input"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder={t("description_placeholder")}
            rows={4}
            className="rounded-md resize-none"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("category_label")}
            </Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="prod-category-select" className="h-11 rounded-md bg-white">
                <SelectValue placeholder={t("category_placeholder")} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("sale_type_label")}
            </Label>
            <Select value={form.sale_type} onValueChange={(v) => set("sale_type", v)}>
              <SelectTrigger data-testid="prod-sale-type-select" className="h-11 rounded-md bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="wholesale" data-testid="sale-type-wholesale">{t("sale_type_wholesale")}</SelectItem>
                <SelectItem value="retail" data-testid="sale-type-retail">{t("sale_type_retail")}</SelectItem>
                <SelectItem value="both" data-testid="sale-type-both">{t("sale_type_both")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("price_per_unit")}
            </Label>
            <Input
              data-testid="prod-price-input"
              value={form.price}
              onChange={(e) => set("price", e.target.value)}
              placeholder={t("price_per_unit_placeholder")}
              className="h-11 rounded-md font-mono-brand"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("moq_label")}
            </Label>
            <Input
              data-testid="prod-moq-input"
              value={form.moq}
              onChange={(e) => set("moq", e.target.value)}
              placeholder={t("moq_placeholder")}
              className="h-11 rounded-md"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("stock_label")}
            </Label>
            <Input
              data-testid="prod-stock-input"
              value={form.stock}
              onChange={(e) => set("stock", e.target.value)}
              placeholder={t("stock_placeholder")}
              className="h-11 rounded-md"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
            {t("seller_name")}
          </Label>
          <Input
            data-testid="prod-seller-name-input"
            value={form.seller_name}
            onChange={(e) => set("seller_name", e.target.value)}
            placeholder={t("seller_name_placeholder")}
            className="h-11 rounded-md"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("email_label")}
            </Label>
            <Input
              data-testid="prod-email-input"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder={t("email_placeholder")}
              className="h-11 rounded-md"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("phone_label")}
            </Label>
            <Input
              data-testid="prod-phone-input"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder={t("phone_placeholder")}
              className="h-11 rounded-md font-mono-brand"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
            {t("photo_label")}
          </Label>
          <MediaUpload
            media={form.media}
            onChange={(m) => set("media", m)}
            testId="prod-media-upload"
          />
        </div>

        <div className="pt-4 border-t border-border">
          <Button
            type="submit"
            data-testid="submit-product-btn"
            disabled={!canSubmit}
            size="lg"
            className="w-full sm:w-auto rounded-md gap-2 h-12 px-8"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> {t("posting")}
              </>
            ) : (
              <>
                <Send className="h-4 w-4" /> {t("submit_product")}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
